import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { ApiLoader } from 'components';

interface LoaderContextType {
  isLoading: boolean;
}

export const LoaderContext = createContext<LoaderContextType>({} as LoaderContextType);

export const LoaderProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isLoading: isAuthLoading } = useAuth0();
  const pendingCount = useRef(0);

  useEffect(() => {
    const begin = (config: InternalAxiosRequestConfig) => {
      if ((config as any).disableLoader) {
        return config;
      }
      pendingCount.current += 1;
      setIsLoading(true);
      return config;
    };

    const end = () => {
      pendingCount.current = Math.max(0, pendingCount.current - 1);
      if (pendingCount.current === 0) {
        setIsLoading(false);
      }
    };

    const requestId = axios.interceptors.request.use(begin, (error: AxiosError) => {
      if (!(error.config as any)?.disableLoader) {
        end();
      }
      return Promise.reject(error);
    });

    const responseId = axios.interceptors.response.use(
      (response: AxiosResponse) => {
        if (!(response.config as any).disableLoader) {
          end();
        }
        return response;
      },
      (error: AxiosError) => {
        if (!(error.config as any)?.disableLoader) {
          end();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, []);

  useEffect(() => {
    if (isAuthLoading) {
      setIsLoading(true);
    } else if (pendingCount.current === 0) {
      setIsLoading(false);
    }
  }, [isAuthLoading]);

  return (
    <LoaderContext.Provider value={{ isLoading }}>
      <ApiLoader />
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => useContext(LoaderContext);
